import classes from 'classnames';
import React, {
  createRef,
  PureComponent,
  ReactNode,
  RefObject,
  SyntheticEvent,
} from 'react';
import styles from './Swiper.module.scss';

export type SwiperProps = {
  defaultValue: number;
  disabled: boolean;
  className?: string;
  swiperClassName?: string;
  heading: string;
  limit?: number;
  limitMessage?: ReactNode;
  max: number;
  min: number;
  onBeforeChange?: Function;
  onChange: Function;
  snap: number;
  activeItemWidth?: number;
  step: number;
  transition: number;
  dataTest?: string;
  unit?: string;
  unitPosition: 'top' | 'right' | 'bottom' | 'left';
};

type State = {
  amplitude: number;
  dragging: boolean;
  frame: number;
  max: number;
  min: number;
  offset: number;
  pressed?: boolean;
  reference: number;
  target: number;
  timestamp: number;
  velocity: number;
  moving: boolean;
};

export default class Swiper extends PureComponent<SwiperProps, State> {
  static defaultProps = {
    disabled: false,
    transition: 150,
    unitPosition: 'right',
  };

  private baseRef: RefObject<HTMLDivElement>;
  private viewRef: RefObject<HTMLDivElement>;
  private ticker: number | undefined;

  constructor(props: SwiperProps) {
    super(props);

    this.baseRef = createRef();
    this.viewRef = createRef();
    this.state = {
      amplitude: 0,
      dragging: false,
      frame: 0,
      max: 0,
      min: 0,
      offset: 0,
      pressed: false,
      reference: 0,
      target: 0,
      timestamp: 0,
      velocity: 0,
      moving: false,
    };
  }

  componentDidMount() {
    const { step, defaultValue, min } = this.props;
    const index: number = (defaultValue - min) / step;

    this.setMaxTransform();
    this.scrollToItem(index);
  }

  componentDidUpdate(prevProps: SwiperProps) {
    const {
      min: prevMin,
      max: prevMax,
      defaultValue: prevDefaultValue,
    } = prevProps;
    const { defaultValue, max, min, step } = this.props;

    if (prevMin !== min || prevMax !== max) {
      this.update();
    }

    if (prevDefaultValue !== defaultValue) {
      const index: number = (defaultValue - min) / step;

      this.scrollToItem(index);
    }
  }

  preventEvent(event: SyntheticEvent) {
    event.preventDefault();
  }

  setMaxTransform() {
    const viewElement = this.viewRef.current;
    const baseElement = this.baseRef.current;

    if (viewElement && baseElement) {
      const max: number = viewElement.offsetWidth - baseElement.offsetWidth;

      this.setState({ max });
    }
  }

  range(start: number, end: number, step: number): Array<number> {
    const length: number = Math.floor((end - start) / step) + 1;

    return Array.from(Array(length), (_, index) => start + index * step);
  }

  update() {
    this.setMaxTransform();

    window.requestAnimationFrame(this.autoScroll);
  }

  position = (event: any): number => {
    if (event.targetTouches && event.targetTouches.length > 0) {
      return event.targetTouches[0].clientX;
    }

    return event.clientX;
  };

  scroll = (x: number) => {
    const { min, max } = this.state;
    let offset: number | null = x > max ? max : null;

    if (!offset) {
      offset = x < min ? min : x;
    }

    const viewElement = this.viewRef.current;

    if (viewElement) {
      viewElement.style.transform = `translate3d(${
        -offset -
        (this.props.activeItemWidth !== undefined
          ? (this.props.activeItemWidth - this.props.snap) / 2
          : 0)
      }px, 0, 0)`;
    }

    this.setState({ offset });
  };

  track = () => {
    const { frame, offset, timestamp, velocity } = this.state;
    const now: number = Date.now();
    const elapsed: number = now - timestamp;
    const delta: number = offset - frame;
    const vector: number = (1000 * delta) / (1 + elapsed);

    this.setState({
      timestamp: now,
      frame: offset,
      velocity: 0.8 * vector + 0.2 * velocity,
    });
  };

  autoScroll = () => {
    const { timestamp, amplitude, target } = this.state;
    let elapsed: number;
    let delta: number;

    if (amplitude) {
      elapsed = Date.now() - timestamp;
      delta = -amplitude * Math.exp(-elapsed / this.props.transition);

      if (delta > 1 || delta < -1) {
        this.scroll(target + delta);
        window.requestAnimationFrame(this.autoScroll);
      } else {
        this.scroll(target);
        this.onChange();
        this.setState({ moving: false });
      }

      this.setState({ dragging: false });
    }
  };

  scrollToItem(index: number) {
    const target: number = index * this.props.snap;
    const amplitude: number = target - this.state.offset;

    this.setState(
      {
        target,
        amplitude,
        timestamp: Date.now(),
      },
      () => this.scroll(target),
    );
  }

  next = () => {
    this.scrollToItem(this.current() + 1);
  };

  current(): number {
    const { snap } = this.props;
    const { offset } = this.state;
    let current: number;

    if (offset === 0) {
      current = 0;
    } else {
      current = Math.round(offset / snap);
    }

    return current;
  }

  handleTap = (event: SyntheticEvent) => {
    if (this.props.min === this.props.max || this.props.disabled) {
      return;
    }

    if (!this.state.moving) {
      this.onBeforeChange();
    }

    this.setState(
      {
        pressed: true,
        moving: true,
        reference: this.position(event),
        velocity: 0,
        amplitude: 0,
        timestamp: Date.now(),
        frame: this.state.offset,
      },
      () => {
        clearInterval(this.ticker);

        this.ticker = window.setInterval(this.track, 100);

        window.addEventListener('mousemove', this.handleDrag);
        window.addEventListener('mouseup', this.handleRelease);
        window.addEventListener('touchmove', this.handleDrag);
        window.addEventListener('touchend', this.handleRelease);
      },
    );
  };

  handleDrag = (event: Event) => {
    let x: number;
    let delta: number;

    if (this.state.pressed) {
      x = this.position(event);
      delta = this.state.reference - x;

      if (delta > 2 || delta < -2) {
        this.setState(
          {
            reference: x,
            dragging: true,
          },
          () => {
            this.scroll(this.state.offset + delta);
          },
        );
      }
    }
  };

  handleRelease = () => {
    if (this.props.min === this.props.max || this.props.disabled) {
      return;
    }

    const { snap } = this.props;
    const { velocity, offset } = this.state;

    this.setState(
      {
        pressed: false,
      },
      () => {
        clearInterval(this.ticker);
      },
    );

    let amplitude: number;
    let target: number = offset;

    if (velocity > 10 || velocity < -10) {
      amplitude = 0.8 * velocity;
      target = Math.round(offset + amplitude);
    }

    target = Math.round(target / snap) * snap;
    amplitude = target - offset;

    this.setState(
      {
        target,
        amplitude,
        timestamp: Date.now(),
      },
      () => {
        window.requestAnimationFrame(this.autoScroll);
        window.removeEventListener('mousemove', this.handleDrag);
        window.removeEventListener('mouseup', this.handleRelease);
        window.removeEventListener('touchmove', this.handleDrag);
        window.removeEventListener('touchend', this.handleRelease);
      },
    );
  };

  getValue(): number {
    return this.props.min + this.current() * this.props.step;
  }

  onChange = () => {
    this.props.onChange(this.getValue());
  };

  onBeforeChange = () => {
    const { onBeforeChange } = this.props;

    if (onBeforeChange) {
      onBeforeChange(this.getValue());
    }
  };

  renderStep(item: number, index: number): ReactNode {
    const {
      dataTest = 'swiperStep',
      disabled,
      max,
      min,
      snap,
      unit,
    } = this.props;
    const isCurrent: boolean = this.current() === index;
    const disabledSwiper: boolean = disabled || min === max;
    const classNames: string = classes(
      styles.item,
      disabledSwiper && styles.disabled,
      isCurrent && styles.active,
    );
    const handleClick = () => {
      if (!this.state.dragging && !disabledSwiper) {
        this.scrollToItem(index);
      }
    };

    return (
      <div
        onClick={handleClick}
        className={classNames}
        key={`${dataTest}-${index}`}
        data-test={`swiper-item-${index}${
          isCurrent ? ' swiper-item-active' : ''
        }`}
        style={{
          width: `${
            isCurrent && this.props.activeItemWidth !== undefined
              ? this.props.activeItemWidth
              : snap
          }px`,
        }}
      >
        <div className={classes(styles.label)}>
          {item}
          <span className={styles.unit}>{unit}</span>
        </div>
      </div>
    );
  }

  renderSteps(): ReactNode {
    const { min, max, step } = this.props;
    const range = this.range(min, max, step);

    return range.map((item, index) => this.renderStep(item, index));
  }

  render() {
    const {
      disabled,
      className,
      swiperClassName,
      heading,
      limit,
      limitMessage,
      max,
      min,
      snap,
      dataTest = 'swiper',
    } = this.props;
    const showLimitMessage: boolean =
      !!limit && !!limitMessage && this.getValue() > limit;
    const disabledSwiper: boolean = disabled || min === max;

    return (
      <div className={className}>
        <div
          data-test="swiper-heading"
          className={classes(styles.heading, disabledSwiper && styles.disabled)}
        >
          {showLimitMessage ? limitMessage : heading}
        </div>
        <div
          data-test={dataTest}
          className={classes(
            styles.wrapper,
            disabledSwiper && styles.disabled,
            showLimitMessage && styles['limit-measurements'],
            swiperClassName,
          )}
        >
          <div
            data-test="swiper-container"
            onMouseDown={this.handleTap}
            onMouseUp={this.handleRelease}
            onTouchStart={this.handleTap}
            onTouchMove={this.preventEvent}
            onTouchEnd={this.handleRelease}
            className={classes(
              styles.container,
              disabledSwiper && styles.disabled,
            )}
          >
            <div
              className={styles.base}
              ref={this.baseRef}
              style={{ width: `${snap}px` }}
            >
              <div className={styles.view} ref={this.viewRef}>
                {this.renderSteps()}
              </div>
            </div>
          </div>

          <div
            className={classes(
              styles.measurements,
              disabledSwiper && styles.disabled,
            )}
          >
            <div className={styles['measurements-right']} />
          </div>

          {showLimitMessage && <div className={styles['limit-measurements']} />}
        </div>
      </div>
    );
  }
}
